import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Save, Trash2, Plus, Loader2, DollarSign } from "lucide-react";

const API = "/api";

export default function AdminPricingDisplay() {
  const [rows, setRows] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const [anchorNote, setAnchorNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRow, setNewRow] = useState({ country: "", currency: "", amount: "" });

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("avisocar_token");
      const r = await axios.get(`${API}/admin/pricing-display`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data.rows || []);
      setAnchor(r.data.anchor || null);
      setAnchorNote(r.data.anchor?.note || "");
    } catch (_e) {
      toast.error("Error al cargar tabla");
    } finally { setLoading(false); }
  };

  const saveAnchor = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("avisocar_token");
      await axios.put(`${API}/admin/pricing-anchor`, { note: anchorNote }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ancla actualizada");
      load();
    } catch (_e) {
      toast.error("Error al guardar ancla");
    } finally { setSaving(false); }
  };

  useEffect(() => { load(); }, []);

  const upsert = async (row) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("avisocar_token");
      await axios.put(`${API}/admin/pricing-display`, row, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Guardado ${row.country}`);
      load();
    } catch (_e) {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async (country) => {
    if (!window.confirm(`¿Eliminar ${country}?`)) return;
    const token = localStorage.getItem("avisocar_token");
    await axios.delete(`${API}/admin/pricing-display/${country}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success(`Eliminado ${country}`);
    load();
  };

  const addNew = () => {
    if (!newRow.country || !newRow.currency) { toast.error("País y moneda requeridos"); return; }
    upsert({ country: newRow.country.toUpperCase(), currency: newRow.currency.toUpperCase(), amount: Number(newRow.amount) || 0 });
    setNewRow({ country: "", currency: "", amount: "" });
  };

  return (
    <div className="min-h-screen bg-[#071B2F] p-6" data-testid="admin-pricing-display">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin" className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
              <DollarSign className="w-6 h-6 text-[#5BC0FF]" /> Precio internacional
            </h1>
            <p className="text-slate-400 text-sm">Importes SOLO de visualización por país. Se muestran en la tarjeta de precio del landing.</p>
          </div>
        </div>

        {/* §18 — la dependencia manual se declara de forma explícita */}
        <div
          className="mb-4 rounded-xl border border-amber-400/25 bg-amber-500/10 p-4 text-sm text-amber-100"
          data-testid="pricing-dependency-notice"
        >
          <p className="font-semibold">El cobro real es SIEMPRE en soles (PEN).</p>
          <p className="mt-1 text-amber-200/90">
            Estos importes son estimaciones aproximadas para que el cliente se oriente: no
            son el importe que se cobra. <strong>Al cambiar el precio de un plan estas
            cifras NO se actualizan solas</strong>: hay que revisarlas y volver a guardarlas
            aquí una por una. No se usan tipos de cambio en vivo.
          </p>
        </div>

        {/* 4-D1 regla 9 — ancla de precios documentada y editable */}
        {anchor && (
          <Card className="bg-amber-950/40 border-amber-800/50 mb-4" data-testid="pricing-anchor-card">
            <CardContent className="p-4 space-y-2">
              <p className="text-amber-300 text-sm font-bold">
                Ancla de la tabla: {anchor.pen_usd_rate} PEN/USD · revisión al llegar a {anchor.review_after_customers} clientes pagos
              </p>
              <textarea
                value={anchorNote}
                onChange={(e) => setAnchorNote(e.target.value)}
                rows={3}
                className="w-full rounded-lg bg-[#0B2545] border border-white/15 text-slate-200 text-xs p-2"
                data-testid="pricing-anchor-note"
              />
              <Button onClick={saveAnchor} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-500" data-testid="pricing-anchor-save">
                <Save className="w-3.5 h-3.5 mr-1" /> Guardar nota de ancla
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="bg-[#0B2545] border-white/10 mb-4">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="País (ej: AR)" value={newRow.country} onChange={(e) => setNewRow({ ...newRow, country: e.target.value })} className="bg-[#14375F] border-white/15 text-white" data-testid="pricing-new-country" />
            <Input placeholder="Moneda (ej: ARS)" value={newRow.currency} onChange={(e) => setNewRow({ ...newRow, currency: e.target.value })} className="bg-[#14375F] border-white/15 text-white" data-testid="pricing-new-currency" />
            <Input placeholder="Monto display" type="number" value={newRow.amount} onChange={(e) => setNewRow({ ...newRow, amount: e.target.value })} className="bg-[#14375F] border-white/15 text-white" data-testid="pricing-new-amount" />
            <Button onClick={addNew} disabled={saving} className="bg-[#1B84FF] hover:bg-[#0F6FDB]" data-testid="pricing-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar / Actualizar
            </Button>
          </CardContent>
        </Card>

        <div className="bg-[#0B2545] rounded-xl border border-white/10 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando...</div>
          ) : (
            <table className="w-full text-sm" data-testid="pricing-display-table">
              <thead className="bg-[#071B2F] sticky top-0">
                <tr className="text-left text-slate-500 border-b border-white/10">
                  <th className="p-3">País</th>
                  <th className="p-3">Moneda</th>
                  <th className="p-3">Monto display</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <EditableRow key={r.country} row={r} onSave={upsert} onDelete={() => remove(r.country)} saving={saving} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function EditableRow({ row, onSave, onDelete, saving }) {
  const [amount, setAmount] = useState(row.amount);
  const [amountAnnual, setAmountAnnual] = useState(row.amount_annual || "");
  const [currency, setCurrency] = useState(row.currency);
  const changed = Number(amount) !== Number(row.amount) || currency !== row.currency || Number(amountAnnual || 0) !== Number(row.amount_annual || 0);
  return (
    <tr className="border-b border-white/10/60 hover:bg-white/10/40" data-testid={`pricing-row-${row.country}`}>
      <td className="p-3 text-white font-mono">{row.country}</td>
      <td className="p-3">
        <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className="bg-[#14375F] border-white/15 text-white h-8 w-20" data-testid={`pricing-row-${row.country}-currency`} />
      </td>
      <td className="p-3">
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-[#14375F] border-white/15 text-white h-8 w-28" data-testid={`pricing-row-${row.country}-amount`} />
        <Input type="number" placeholder="Anual" value={amountAnnual} onChange={(e) => setAmountAnnual(e.target.value)} className="bg-[#14375F] border-white/15 text-white h-8 w-28" data-testid={`pricing-row-${row.country}-amount-annual`} />
      </td>
      <td className="p-3 text-right flex justify-end gap-1">
        <Button
          size="sm"
          onClick={() => onSave({ country: row.country, currency, amount: Number(amount), amount_annual: amountAnnual ? Number(amountAnnual) : null })}
          disabled={!changed || saving}
          className="bg-[#1B84FF] hover:bg-[#1B84FF] h-8"
          data-testid={`pricing-row-${row.country}-save`}
        >
          <Save className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="text-red-400 hover:text-red-300 h-8"
          data-testid={`pricing-row-${row.country}-delete`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </td>
    </tr>
  );
}
